import { useState } from 'react'
import { App as AntdApp, Button, Collapse, Input, InputNumber, Space, Table, Tag, Typography, Switch, Descriptions, Alert } from 'antd'
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import { useSlaRules, useUpdateSlaRule } from '@/modules/sla/queries'
import {
  getSlaRuleDescription,
  getSlaRuleName,
  getSlaStageLabel,
  getSlaTargetRoleLabel,
} from '@/modules/sla/labels'
import ErrorResult from '@/components/feedback/ErrorResult'
import type { SlaRuleItem, UpdateSlaRulePayload } from '@/types/sla'

const { Text, Title, Paragraph } = Typography

function buildBusinessRuleSummary(rules: SlaRuleItem[]) {
  const enabledRules = rules.filter((r) => r.enabled)
  const escalationRules = enabledRules.filter((r) => r.escalation_enabled)

  return {
    enabledRules,
    escalationRules,
  }
}

export default function SlaRuleManagementPage() {
  const { message } = AntdApp.useApp()
  const [drafts, setDrafts] = useState<Record<number, UpdateSlaRulePayload>>({})
  const { data, isLoading, isError, error, refetch } = useSlaRules()
  const updateRule = useUpdateSlaRule()

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  const patchDraft = (ruleId: number, patch: UpdateSlaRulePayload) => {
    setDrafts((current) => ({
      ...current,
      [ruleId]: {
        ...(current[ruleId] || {}),
        ...patch,
      },
    }))
  }

  const getThresholdMinutes = (rule: SlaRuleItem) =>
    drafts[rule.id]?.threshold_minutes ?? rule.threshold_minutes

  const getRemindIntervalMinutes = (rule: SlaRuleItem) =>
    drafts[rule.id]?.remind_interval_minutes ?? rule.remind_interval_minutes

  const getEnabled = (rule: SlaRuleItem) =>
    drafts[rule.id]?.enabled ?? rule.enabled

  const getEscalationEnabled = (rule: SlaRuleItem) =>
    drafts[rule.id]?.escalation_enabled ?? rule.escalation_enabled

  const getEscalationAfterMinutes = (rule: SlaRuleItem) =>
    drafts[rule.id]?.escalation_after_minutes ?? rule.escalation_after_minutes ?? rule.remind_interval_minutes

  const getEscalationCcEmails = (rule: SlaRuleItem) =>
    drafts[rule.id]?.escalation_cc_emails ?? rule.escalation_cc_emails

  const hasDraft = (ruleId: number) => Object.keys(drafts[ruleId] || {}).length > 0

  const saveRule = (rule: SlaRuleItem) => {
    const payload = drafts[rule.id]
    if (!payload || Object.keys(payload).length === 0) return
    updateRule.mutate(
      { ruleId: rule.id, payload },
      {
        onSuccess: () => {
          setDrafts((current) => {
            const next = { ...current }
            delete next[rule.id]
            return next
          })
          message.success('响应时限规则已保存')
        },
      },
    )
  }

  const rules = data || []
  const { enabledRules, escalationRules } = buildBusinessRuleSummary(rules)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Title level={4} style={{ margin: 0 }}>响应时限设置</Title>
        <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
          刷新
        </Button>
      </Space>

      <Collapse
        size="small"
        items={[
          {
            key: 'business-rules',
            label: <Text strong>业务规则说明</Text>,
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Alert
                  type="info"
                  showIcon
                  message="整体流程"
                  description={
                    <Paragraph style={{ margin: 0 }}>
                      系统定时扫描各环节的待处理事项，将超时未处理的对象生成<Text code>SlaEvent</Text>（状态：<Tag color="blue">open</Tag>）。
                      通知作业独立于扫描运行，读取所有活跃事件，按规则配置的<Text strong>提醒间隔</Text>周期性发送告警通知。
                      当违规条件消失（如消息已回复、工单已处理），事件自动<Text code>resolved</Text>；也可手动<Text code>ignored</Text>或<Text code>resolved</Text>。
                    </Paragraph>
                  }
                />

                <Descriptions
                  bordered
                  size="small"
                  column={2}
                  title="告警触发规则"
                >
                  <Descriptions.Item label="不处理的情况" span={2}>
                    <Paragraph style={{ margin: 0 }}>
                      1. 规则状态为<Text code>enabled=false</Text>（停用）；<br />
                      2. 当前无超时对象（检测器未发现违规）；<br />
                      3. 事件已被手动<Text code>ignored</Text>（忽略）或<Text code>resolved</Text>（已解决）；<br />
                      4. 距上次提醒未满<Text strong>提醒间隔</Text>时长（防止重复骚扰）。
                    </Paragraph>
                  </Descriptions.Item>
                  <Descriptions.Item label="触发条件">
                    对象在对应环节停留时间超过<Text strong>超时阈值</Text>后，首次检测即创建事件。
                  </Descriptions.Item>
                  <Descriptions.Item label="事件自动关闭">
                    当检测器再次扫描时发现违规条件已消失（如消息已回复），自动将事件标记为<Text code>resolved</Text>。
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  title="二级升级机制"
                >
                  <Descriptions.Item label="升级流程">
                    <Paragraph style={{ margin: 0 }}>
                      1. <Text strong>一级告警（L1）</Text>：事件创建后，通知作业首次发送即标记为 L1，通知目标角色对应的负责人。<br />
                      2. <Text strong>二级升级（L2）</Text>：L1 发送后，若在<Text strong>升级等待</Text>时长内事件仍未解决，自动触发 L2 升级告警，
                      通知同一负责人并<Text strong>抄送升级邮箱</Text>。
                      {escalationRules.length > 0 && (
                        <> 当前启用了升级的规则：{escalationRules.map((r) => getSlaRuleName(r.rule_code, r.rule_name)).join('、')}。</>
                      )}
                      {escalationRules.length === 0 && ' 当前无规则启用二级升级。'}
                    </Paragraph>
                  </Descriptions.Item>
                  <Descriptions.Item label="重复提醒">
                    L1/L2 均按<Text strong>提醒间隔</Text>周期重复发送，直到事件被关闭。每次提醒均记录在<Text code>remind_count</Text>中。
                  </Descriptions.Item>
                </Descriptions>

                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  title="通知方式与对象"
                >
                  <Descriptions.Item label="通知渠道">
                    由服务端 <Text code>NOTIFICATION_CHANNEL</Text> 配置决定，当前支持
                    <Text code>system</Text>（仅日志记录）和 <Text code>email</Text>（SMTP 邮件）。
                    实际生效渠道以部署环境配置为准。
                  </Descriptions.Item>
                  <Descriptions.Item label="通知对象">
                    一级告警通知<Text strong>目标角色</Text>对应的负责人账号（如接待负责人）。
                    二级升级在通知同一对象的同时，抄送规则中配置的<Text strong>升级抄送邮箱</Text>。
                  </Descriptions.Item>
                  <Descriptions.Item label="当前规则概览">
                    {enabledRules.length === 0 ? (
                      '暂无启用的规则'
                    ) : (
                      <Space orientation="vertical" size={4}>
                        {enabledRules.map((rule) => (
                          <Text key={rule.id}>
                            <Text strong>{getSlaRuleName(rule.rule_code, rule.rule_name)}</Text>
                            ：超时 {rule.threshold_minutes} 分钟后触发，每 {rule.remind_interval_minutes} 分钟重复提醒
                            {rule.escalation_enabled
                              ? `，${rule.escalation_after_minutes ?? rule.remind_interval_minutes} 分钟后升级 L2 并抄送 ${rule.escalation_cc_emails || 'qhdet@cleartv.cn'}`
                              : '，未启用二级升级'}
                          </Text>
                        ))}
                      </Space>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            ),
          },
        ]}
      />

      <Table<SlaRuleItem>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data || []}
        pagination={false}
        columns={[
          {
            title: '规则',
            dataIndex: 'rule_code',
            width: 180,
            render: (ruleCode: string, rule: SlaRuleItem) => (
              <Space orientation="vertical" size={0}>
                <Text strong>{getSlaRuleName(ruleCode, rule.rule_name)}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{ruleCode}</Text>
              </Space>
            ),
          },
          {
            title: '业务意义',
            dataIndex: 'rule_code',
            width: 360,
            render: (ruleCode: string) => (
              <Text type="secondary">{getSlaRuleDescription(ruleCode)}</Text>
            ),
          },
          {
            title: '环节',
            dataIndex: 'stage',
            width: 110,
            render: (value: string) => getSlaStageLabel(value),
          },
          {
            title: '提醒对象',
            dataIndex: 'target_role',
            width: 120,
            render: (value: string) => getSlaTargetRoleLabel(value),
          },
          {
            title: '超时阈值',
            dataIndex: 'threshold_minutes',
            width: 160,
            render: (_: number, rule: SlaRuleItem) => (
              <InputNumber
                min={1}
                precision={0}
                addonAfter="分钟"
                value={getThresholdMinutes(rule)}
                onChange={(value) => {
                  if (typeof value === 'number') patchDraft(rule.id, { threshold_minutes: value })
                }}
                style={{ width: 130 }}
              />
            ),
          },
          {
            title: '提醒间隔',
            dataIndex: 'remind_interval_minutes',
            width: 160,
            render: (_: number, rule: SlaRuleItem) => (
              <InputNumber
                min={1}
                precision={0}
                addonAfter="分钟"
                value={getRemindIntervalMinutes(rule)}
                onChange={(value) => {
                  if (typeof value === 'number') patchDraft(rule.id, { remind_interval_minutes: value })
                }}
                style={{ width: 130 }}
              />
            ),
          },
          {
            title: '二级升级',
            dataIndex: 'escalation_enabled',
            width: 120,
            render: (_: boolean, rule: SlaRuleItem) => {
              const enabled = getEscalationEnabled(rule)
              return (
                <Space>
                  <Switch
                    checked={enabled}
                    onChange={(checked) => patchDraft(rule.id, { escalation_enabled: checked })}
                  />
                  <Tag color={enabled ? 'warning' : 'default'}>{enabled ? '启用' : '停用'}</Tag>
                </Space>
              )
            },
          },
          {
            title: '升级等待',
            dataIndex: 'escalation_after_minutes',
            width: 160,
            render: (_: number | null, rule: SlaRuleItem) => (
              <InputNumber
                min={1}
                precision={0}
                addonAfter="分钟"
                disabled={!getEscalationEnabled(rule)}
                value={getEscalationAfterMinutes(rule)}
                onChange={(value) => {
                  if (typeof value === 'number') patchDraft(rule.id, { escalation_after_minutes: value })
                }}
                style={{ width: 130 }}
              />
            ),
          },
          {
            title: '二级抄送',
            dataIndex: 'escalation_cc_emails',
            width: 220,
            render: (_: string, rule: SlaRuleItem) => (
              <Input
                disabled={!getEscalationEnabled(rule)}
                value={getEscalationCcEmails(rule)}
                onChange={(event) => patchDraft(rule.id, { escalation_cc_emails: event.target.value })}
                placeholder="qhdet@cleartv.cn"
              />
            ),
          },
          {
            title: '状态',
            dataIndex: 'enabled',
            width: 120,
            render: (_: boolean, rule: SlaRuleItem) => {
              const enabled = getEnabled(rule)
              return (
                <Space>
                  <Switch
                    checked={enabled}
                    onChange={(checked) => patchDraft(rule.id, { enabled: checked })}
                  />
                  <Tag color={enabled ? 'success' : 'default'}>{enabled ? '启用' : '停用'}</Tag>
                </Space>
              )
            },
          },
          {
            title: '操作',
            width: 110,
            render: (_: unknown, rule: SlaRuleItem) => (
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                disabled={!hasDraft(rule.id)}
                loading={updateRule.isPending && updateRule.variables?.ruleId === rule.id}
                onClick={() => saveRule(rule)}
              >
                保存
              </Button>
            ),
          },
        ]}
        scroll={{ x: 1780 }}
      />
    </div>
  )
}
